// ============================================================================
// FSA TRIAL BALANCE HOOKS
// ============================================================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fsaTrialBalanceService } from 'api/services/fsa/fsaTrialBalanceService';
import type { ID, PaginatedResponse } from 'api/types';
import type {
  TrialBalance,
  TrialBalanceEntry,
  TrialBalanceEntryQuery,
} from 'api/types/fsa/trialBalance.types';

const EMPTY_PAGINATION = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/** Current (latest version) TB for an engagement. `data` is null when not yet uploaded. */
export const useCurrentTrialBalance = (engagementId: ID | null | undefined) => {
  const [data, setData] = useState<TrialBalance | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!engagementId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fsaTrialBalanceService.getCurrent(engagementId);
      setData(response.data ?? null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => { refetch(); }, [refetch]);

  return { data, loading, error, refetch };
};

/** All TB versions for an engagement (version history). */
export const useTrialBalanceVersions = (engagementId: ID | null | undefined) => {
  const [data, setData] = useState<TrialBalance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!engagementId) {
      setData([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fsaTrialBalanceService.listByEngagement(engagementId);
      setData(response.data ?? []);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => { refetch(); }, [refetch]);

  return { data, loading, error, refetch };
};

/** Paged TB entries with classification filter. */
export const useTrialBalanceEntries = (
  trialBalanceId: ID | null | undefined,
  query?: TrialBalanceEntryQuery
) => {
  const [data, setData] = useState<TrialBalanceEntry[]>([]);
  const [pagination, setPagination] =
    useState<PaginatedResponse<TrialBalanceEntry>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const queryKey = useMemo(() => JSON.stringify(query ?? {}), [query]);

  const refetch = useCallback(async () => {
    if (!trialBalanceId) {
      setData([]);
      setPagination(EMPTY_PAGINATION);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fsaTrialBalanceService.getEntries(trialBalanceId, query);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trialBalanceId, queryKey]);

  useEffect(() => { refetch(); }, [refetch]);

  return { data, pagination, loading, error, refetch };
};
